import React, { memo, useMemo } from 'react';
import { Pressable, ScrollView } from 'react-native';

import { Stack } from '@/components/layout';
import {
  PlayerProfileDetail,
  type PlayerProfileDetailRow,
} from '@/components/player-profile-detail';
import { SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { CoGuardianMutualKidVM } from '@/features/guardian-player/types';
import { levelColor } from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.coGuardianProfile.detailSheet;

type MutualKidDetailSheetProps = {
  open: boolean;
  kid: CoGuardianMutualKidVM | null;
  onOpenChange: (open: boolean) => void;
};

/** Builds the label/value rows shown in the detail card, skipping any field the API omitted. */
function buildDetailRows(kid: CoGuardianMutualKidVM | null): PlayerProfileDetailRow[] {
  if (!kid) return [];
  return (
    [
      { label: strings.birthdate, value: kid.birthdateLabel },
      { label: strings.gender, value: kid.genderLabel },
      { label: strings.height, value: kid.heightLabel },
      { label: strings.dominantHand, value: kid.dominantHandLabel },
      { label: strings.address, value: kid.addressLabel },
      { label: strings.zipCode, value: kid.zipcodeLabel },
    ] as { label: string; value?: string }[]
  ).filter((row): row is PlayerProfileDetailRow => Boolean(row.value));
}

/** "8 years old" / "1 year old", or null when the age is unknown. */
function buildAgeLabel(age: number | null): string | null {
  if (age === null || age === undefined) return null;
  return `${age} ${age === 1 ? strings.ageYearOld : strings.ageYearsOld}`;
}

/**
 * "Player Profile Detail" summary for a mutual kid — a bottom sheet on phone and a side
 * tab on tablet (both via {@link SheetContent}). Phone closes via the ✕ in the header;
 * tablet closes via the "Close" button pinned under the content.
 */
function MutualKidDetailSheet({ open, kid, onOpenChange }: MutualKidDetailSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const close = () => onOpenChange(false);

  const level = kid?.level ?? 1;
  const detailRows = useMemo(() => buildDetailRows(kid), [kid]);

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent
        tabletWidth={scale(418)}
        closeOnBackdropPress
        enableDragToClose
        style={isTablet ? styles.sheet : [styles.sheet, styles.sheetPhone]}>
        <Stack style={styles.container}>
          {isTablet ? (
            <Stack align="center" style={styles.header}>
              <Text
                variant="Font Guardian App/Heading/Heading H6 18px"
                numberOfLines={1}
                style={styles.title}>
                {strings.title}
              </Text>
            </Stack>
          ) : (
            <Stack direction="row" align="center" style={styles.header}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={strings.close}
                onPress={close}
                hitSlop={scale(12)}
                style={styles.closeButtonPhone}>
                <SvgIcon name="IcXClose" size={scale(24)} color={guardianColors['10']} />
              </Pressable>

              <Stack flex={1} align="center">
                <Text
                  variant="Font Guardian App/Heading/Heading H6 18px"
                  numberOfLines={1}
                  style={styles.title}>
                  {strings.title}
                </Text>
              </Stack>

              <Stack style={styles.headerSpacer} />
            </Stack>
          )}

          {kid ? (
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.bodyContent}>
              <PlayerProfileDetail
                avatarUrl={kid.avatarUrl}
                name={kid.name}
                level={level}
                levelBadgeColor={levelColor(level)}
                ageLabel={buildAgeLabel(kid.age)}
                rows={detailRows}
              />
            </ScrollView>
          ) : null}

          {isTablet ? (
            <Stack direction="row" justify="center" style={styles.footer}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={strings.close}
                onPress={close}
                style={({ pressed }) => [
                  styles.closeButtonTablet,
                  pressed ? styles.closeButtonTabletPressed : null,
                ]}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                  style={styles.closeButtonTabletLabel}>
                  {strings.close}
                </Text>
              </Pressable>
            </Stack>
          ) : null}
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

export default memo(MutualKidDetailSheet);

const useStyles = createStyles(({ scale }) => ({
  sheet: {
    backgroundColor: colors['0'],
  },
  sheetPhone: {
    height: '70%',
    borderTopLeftRadius: scale(20),
    borderTopRightRadius: scale(20),
  },
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: scale(16),
    paddingVertical: scale(16),
  },
  headerSpacer: {
    width: scale(40),
  },
  closeButtonPhone: {
    width: scale(40),
    height: scale(40),
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  title: {
    color: guardianColors['10'],
  },
  bodyContent: {
    paddingHorizontal: scale(16),
    paddingBottom: scale(24),
    paddingTop: scale({ phone: 0, tablet: 16 }),
  },
  footer: {
    paddingTop: scale(12),
  },
  closeButtonTablet: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: scale(184),
    gap: scale(8),
    padding: scale(10),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderWidth: 0.5,
    borderColor: guardianColors['Main/Main'],
    backgroundColor: colors['0'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  closeButtonTabletPressed: {
    opacity: 0.7,
  },
  closeButtonTabletLabel: {
    color: guardianColors['Main/Main'],
  },
}));
